/**
 * Capacity clamping for planned booking slots.
 *
 * The planner sizes a slot from the backend's availability breakdown, while
 * `validateSlotEdit` judges it against the resource's working profile less the
 * hours already booked (`rangeCapacityForResource`). The two figures can differ
 * by a few hours, which used to leave an auto-generated plan hard-blocked by
 * `hours-exceed-capacity` on slots the user never touched.
 *
 * The helpers here measure capacity with the validator's own functions and the
 * validator's own inputs, and reduce an over-sized slot down to it — so a plan
 * is valid by construction. They only ever remove hours: a slot's start day is
 * never moved earlier and its range is never widened.
 */

import { isWorkingHoursProfileConfigured } from '@/utils/heatmap-utils';
import { indexBookingsByResource } from '@/lib/recommendationEngine';
import { isDateWithinContract } from '@/lib/contractWindow';
import { Booking, Resource, WorkingHoursProfile } from '@/types';
import type { DailyAllocation, ProposedBookingSlot } from '@/types/resourceRequest';
import {
  bookedHoursByDay,
  dayKey,
  effectiveWorkingProfile,
  profileHoursForDate,
  rangeCapacityForResource,
  WORKING_HOURS_PER_DAY,
  type ReportedDayHours,
  type UnavailableDay,
} from '@/lib/slotEditValidation';

/** Hours are planned and displayed to a tenth of an hour. */
function roundHours(hours: number): number {
  return Math.round(hours * 10) / 10;
}

/** The parts of a resource a working profile is read from. */
type ProfileResource = Pick<
  Resource, 'id' | 'useCustomWorkingProfile' | 'customWorkingProfile' | 'contractStartDate' | 'contractEndDate'
>;

/** The parts of a slot the clamp reads and rewrites. */
export interface CapacityClampableSlot {
  /** Inclusive slot range, 'yyyy-MM-dd'. */
  startDate: string;
  endDate: string;
  hours: number;
  dailyBreakdown?: DailyAllocation[];
}

export interface CapacityLookupInput {
  existingBookings: Booking[];
  resources: ProfileResource[];
  globalWorkingProfile?: WorkingHoursProfile;
  /** The backend's per-day booked hours, keyed by resource id. */
  reportedBookedDaysByResourceId?: Record<string, ReportedDayHours[]>;
  /** Bank holidays and absences that take hours off a day, keyed by resource id. */
  unavailableDaysByResourceId?: Record<string, UnavailableDay[]>;
}

/** The capacity questions the validator asks of a slot, for one resource. */
export interface ResourceCapacity {
  /** Hours still free across an inclusive 'yyyy-MM-dd' range. */
  hoursInRange: (startDate: string, endDate: string) => number;
  /** Hours still free on a single 'yyyy-MM-dd' day. */
  hoursOnDay: (date: string) => number;
}

/** A resource's capacity, measured exactly as the validator measures it. */
export type CapacityLookup = (resourceId: string) => ResourceCapacity;

/**
 * A capacity lookup that answers exactly what `validatePlanSlots` will ask: the
 * same working profile (the resource's own when it has one, else the company's)
 * and the same existing-bookings and reported-booked-days inputs.
 */
export function capacityLookup(input: CapacityLookupInput): CapacityLookup {
  const {
    existingBookings, resources, globalWorkingProfile,
    reportedBookedDaysByResourceId, unavailableDaysByResourceId,
  } = input;
  const resourceById = new Map(resources.map(r => [String(r.id), r]));

  const bookedByResourceId = new Map<string, Map<string, number>>();
  let bookingsByResourceId: Map<string, Booking[]> | undefined;
  const bookingsFor = (resourceId: string): Booking[] => {
    bookingsByResourceId ??= indexBookingsByResource(existingBookings);
    return bookingsByResourceId.get(resourceId) ?? [];
  };

  return resourceId => {
    const profile = effectiveWorkingProfile(resourceById.get(resourceId), globalWorkingProfile);
    const reportedDays = reportedBookedDaysByResourceId?.[resourceId];
    const unavailableDays = unavailableDaysByResourceId?.[resourceId];
    // With no working hours configured anywhere there is no capacity to measure
    // against, so the plan is left as the backend sized it.
    if (!isWorkingHoursProfileConfigured(profile)) return UNMEASURED_CAPACITY;
    const bookedByDay = () => {
      const known = bookedByResourceId.get(resourceId);
      if (known) return known;
      const booked = bookedHoursByDay(bookingsFor(resourceId), resourceId, reportedDays);
      bookedByResourceId.set(resourceId, booked);
      return booked;
    };

    return {
      hoursInRange: (startDate, endDate) => {
        const resource = resourceById.get(resourceId);
        const contractStart = resource?.contractStartDate && startDate < resource.contractStartDate
          ? resource.contractStartDate
          : startDate;
        const contractEnd = resource?.contractEndDate && endDate > resource.contractEndDate
          ? resource.contractEndDate
          : endDate;
        const start = parseDay(contractStart);
        const end = parseDay(contractEnd);
        if (!start || !end || end < start) return 0;
        return rangeCapacityForResource(
          bookingsFor(resourceId),
          resourceId,
          start,
          end,
          WORKING_HOURS_PER_DAY,
          profile,
          reportedDays,
          unavailableDays,
        ).availableHours;
      },
      hoursOnDay: date => {
        const day = parseDay(date);
        if (!day) return 0;
        const booked = bookedByDay().get(dayKey(day)) ?? 0;
        const contracted = isDateWithinContract(resourceById.get(resourceId), day)
          ? profileHoursForDate(day, profile)
          : 0;
        const lost = (unavailableDays ?? [])
          .filter(unavailable => unavailable.date.slice(0, 10) === dayKey(day))
          .reduce((most, unavailable) => Math.max(most, unavailable.lostHours ?? contracted), 0);
        return roundHours(Math.max(0, contracted - booked - lost));
      },
    };
  };
}

/** Capacity nothing can exceed, for a resource with no working profile at all. */
const UNMEASURED_CAPACITY: ResourceCapacity = {
  hoursInRange: () => Infinity,
  hoursOnDay: () => Infinity,
};

/**
 * Local midnight of a 'yyyy-MM-dd' day, or null when it is not a date.
 *
 * `new Date('2026-07-08')` reads as UTC midnight, which is the previous day for
 * anywhere behind UTC — and the validator walks days in local time.
 */
function parseDay(date: string): Date | null {
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(date.trim());
  if (!parts) return null;
  return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
}

/** The 'yyyy-MM-dd' a breakdown day names, whatever precision it carries. */
function dayOf(date: string): string {
  return date.slice(0, 10);
}

/** A day's window shortened to the hours it keeps. */
function withShortenedWindow(day: DailyAllocation, hours: number): DailyAllocation {
  const start = new Date(day.startTime);
  if (isNaN(start.getTime())) return { ...day, hours };
  return {
    ...day,
    hours,
    endTime: new Date(start.getTime() + hours * 3_600_000).toISOString(),
  };
}

/** Every day reduced to the hours its resource still has free on it. */
function clampDaysToDayCapacity(
  days: DailyAllocation[],
  capacity: ResourceCapacity,
): { days: DailyAllocation[]; changed: boolean } {
  let changed = false;
  const clamped: DailyAllocation[] = [];

  for (const day of days) {
    const free = capacity.hoursOnDay(dayOf(day.date));
    const hours = roundHours(day.hours);
    if (hours <= free) {
      clamped.push(day);
      continue;
    }
    changed = true;
    if (free <= 0) continue;
    clamped.push(withShortenedWindow(day, free));
  }

  return { days: clamped, changed };
}

/** Drops `excess` hours off the end of a breakdown, shortening the last day it reaches. */
function trimTrailingHours(days: DailyAllocation[], excess: number): DailyAllocation[] {
  const kept = [...days];
  let remaining = excess;
  while (remaining > 0 && kept.length > 0) {
    const last = kept[kept.length - 1];
    const hours = roundHours(last.hours);
    if (hours <= remaining) {
      kept.pop();
      remaining = roundHours(remaining - hours);
      continue;
    }
    kept[kept.length - 1] = withShortenedWindow(last, roundHours(hours - remaining));
    remaining = 0;
  }
  return kept;
}

/**
 * Reduces a slot to the hours its resource really has free — per day, and across
 * the slot's range as a whole — trimming trailing day windows so the breakdown
 * still sums to the slot's total.
 *
 * Returns the slot unchanged when it already fits, and null when nothing is left
 * to allocate (the caller carries the hours elsewhere).
 */
export function clampSlotToCapacity<T extends CapacityClampableSlot>(
  slot: T,
  capacity: ResourceCapacity,
): T | null {
  const breakdown = slot.dailyBreakdown;

  if (!breakdown || breakdown.length === 0) {
    const available = capacity.hoursInRange(slot.startDate, slot.endDate);
    if (roundHours(slot.hours) <= available) return slot;
    if (available <= 0) return null;
    return { ...slot, hours: available };
  }

  const perDay = clampDaysToDayCapacity(breakdown, capacity);
  let days = perDay.days;
  let trimmed = perDay.changed;

  // Trimming trailing days shortens the range, which can in turn lower the
  // capacity that range has, so the fit is re-measured until it holds.
  for (let pass = 0; pass <= breakdown.length; pass++) {
    if (days.length === 0) return null;
    const startDate = dayOf(days[0].date);
    const endDate = dayOf(days[days.length - 1].date);
    const total = roundHours(days.reduce((sum, day) => sum + day.hours, 0));
    const available = capacity.hoursInRange(startDate, endDate);
    if (total <= available) {
      if (!trimmed) return slot;
      return { ...slot, startDate, endDate, hours: total, dailyBreakdown: days };
    }
    days = trimTrailingHours(days, roundHours(total - available));
    trimmed = true;
  }

  return null;
}

/**
 * Reduces the plan's suggested slots to the capacity their resources really
 * have, so residual drift between the planner and the validator does not block a
 * plan nobody edited.
 *
 * Slots the user has edited are returned untouched: an edit that exceeds
 * capacity is the user's own doing and must still block.
 */
export function clampSuggestedSlotsToCapacity(input: CapacityLookupInput & {
  slots: ProposedBookingSlot[];
  /** Ids of slots the user has adjusted in the drawer. */
  editedSlotIds?: ReadonlySet<string>;
}): ProposedBookingSlot[] {
  const { slots, editedSlotIds, ...lookupInput } = input;
  const capacityFor = capacityLookup(lookupInput);

  return slots.map(slot => {
    if (editedSlotIds?.has(slot.id)) return slot;
    // A slot with nothing left to allocate is kept as it is: the validation pass
    // reports it as a conflict, which is a real block rather than drift.
    return clampSlotToCapacity(slot, capacityFor(String(slot.resourceId))) ?? slot;
  });
}
